import React, { useState } from 'react'
import { useRouter } from 'next/router'

import { useActionless, useAppSelector } from 'redux/hooks'

import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'

import { setCollectionV2View } from 'services/caching.service'

import PhatDropdown from 'components/formElements/PhatDropdown'
import { PriceDropdownGroup } from 'components/misc/PriceDropdown'

import { setCollectionQueryParams } from 'utils/queryParams/collection.queryParams'

import styles from './viewsAndSorts.module.scss'
import { PRICE_CODES } from 'types/active'

type Props = {}

const ViewsAndSorts = ({}: Props) => {
  const router = useRouter()

  const { collectionOrderBy = 'updatedAt', orderDirection = 'descending' } = router.query

  const view = useAppSelector(state => state.collectionV2.view)
  const viewingLabels = useAppSelector(state => state.collectionV2.viewingLabels)
  const priceSource = useAppSelector(state => state.collectionV2.priceSource)
  const priceSource2 = useAppSelector(state => state.collectionV2.priceSource2)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)

  const [orderByDesc, setOrderByDesc] = useState(orderDirection === 'descending')

  const handleSetView = (view: 'text' | 'image') => {
    setCollectionV2State({ view, viewingLabels: false })
    setCollectionV2View(view)
  }

  const handleSetOrder = (collectionOrderBy: string, selectedSource?: number) => {
    // @ts-ignore
    const priceSource = PRICE_CODES[selectedSource] || undefined // undefined is fine

    setCollectionQueryParams({
      collectionOrderBy,
      orderDirection: orderByDesc ? 'descending' : 'ascending',
      priceSource,
    })
  }

  const handleChangePriceSource = (source: 'priceSource' | 'priceSource2', priceSource: number) => {
    setCollectionV2State({ [source]: priceSource })

    if (router.query.collectionOrderBy !== 'priceOne' && router.query.collectionOrderBy !== 'priceTwo') return

    const urlKey = PRICE_CODES[priceSource]

    if (
      (router.query.collectionOrderBy === 'priceOne' && source === 'priceSource') ||
      (router.query.collectionOrderBy === 'priceTwo' && source === 'priceSource2')
    )
      setCollectionQueryParams({ priceSource: urlKey })
  }

  return (
    <div className={styles.container}>
      <div className={styles.groupOne}>
        <PhatDropdown
          header="View as"
          value={viewingLabels ? 'labels' : view}
          icon="eye"
          options={[
            { label: 'Text', id: 'text', onClick: () => handleSetView('text') },
            { label: 'Image', id: 'image', onClick: () => handleSetView('image') },
            { type: 'spacer' },
            { label: 'Labels', id: 'labels', onClick: () => setCollectionV2State({ viewingLabels: true }) },
          ]}
        />

        <PhatDropdown
          header="Sort by"
          value={collectionOrderBy}
          triggerWrapperClassName={styles.orderBy}
          menuClassName={styles.orderByMenu}
          icon={orderByDesc ? 'sort amount down' : 'sort amount up'}
          options={[
            {
              type: 'checkbox',
              label: 'Sort descending',
              onChange: () => setOrderByDesc(!orderByDesc),
              checked: orderByDesc,
            },
            { type: 'spacer' },
            {
              label: 'Date updated',
              icon: 'edit outline',
              id: 'updatedAt',
              onClick: () => handleSetOrder('updatedAt'),
            },
            {
              label: 'Date added',
              icon: 'calendar alternate outline',
              id: 'createdAt',
              onClick: () => handleSetOrder('createdAt'),
            },

            { type: 'spacer' },
            {
              label: 'Quantity',
              icon: 'numbered list',
              id: 'quantity',
              onClick: () => handleSetOrder('quantity'),
            },
            {
              label: 'Card name',
              icon: 'font',
              id: 'name',
              onClick: () => handleSetOrder('name'),
            },
            {
              label: 'Rarity',
              icon: 'star',
              id: 'rarity',
              onClick: () => handleSetOrder('rarity'),
            },
            {
              label: 'Type',
              icon: 'tag',
              id: 'type',
              onClick: () => handleSetOrder('type'),
            },
            {
              label: 'Edition name',
              icon: 'clone',
              id: 'set',
              onClick: () => handleSetOrder('set'),
            },
            {
              label: 'Edition date',
              icon: 'calendar alternate outline',
              id: 'editionDate',
              onClick: () => handleSetOrder('editionDate'),
            },
            {
              label: 'Mana value',
              icon: 'sort numeric down',
              id: 'manaValue',
              onClick: () => handleSetOrder('manaValue'),
            },
            {
              label: 'Power',
              icon: 'bolt',
              id: 'power',
              onClick: () => handleSetOrder('power'),
            },
            {
              label: 'Toughness',
              icon: 'shield alternate',
              id: 'toughness',
              onClick: () => handleSetOrder('toughness'),
            },
            {
              label: 'Loyalty',
              icon: 'heart',
              id: 'loyalty',
              onClick: () => handleSetOrder('loyalty'),
            },
            {
              label: 'Artist',
              icon: 'paint brush',
              id: 'artist',
              onClick: () => handleSetOrder('artist'),
            },
            { type: 'spacer' },
            {
              label: 'Price 1',
              icon: 'dollar sign',
              id: 'priceOne',
              onClick: () => handleSetOrder('priceOne', priceSource),
            },
            {
              label: 'Price 2',
              icon: 'dollar sign',
              id: 'priceTwo',
              onClick: () => handleSetOrder('priceTwo', priceSource2),
            },
            { type: 'spacer' },
            {
              label: 'Random',
              icon: 'random',
              id: 'random',
              onClick: () => handleSetOrder('random'),
            },
          ]}
        />
      </div>

      <PriceDropdownGroup
        priceSources={[
          { priceSource, changePriceSource: priceSource => handleChangePriceSource('priceSource', priceSource) },
          {
            priceSource: priceSource2,
            changePriceSource: priceSource2 => handleChangePriceSource('priceSource2', priceSource2),
          },
        ]}
      />
    </div>
  )
}

export default ViewsAndSorts
